/// <reference types="forge-viewer" />
// @ts-check
// Shared Autodesk Viewer instance
/**
 * @module useViewer
 * @description Custom hook to manage Autodesk Viewer instance and highlight objects.
 * This module provides a way to set and access the viewer instance globally,
 * and to highlight objects in the viewer by their dbIds.
 */

/**
 * @typedef {Autodesk.Viewing.GuiViewer3D} GuiViewer3D
 */

/** @type {GuiViewer3D|null} */
let viewerInstance = null;

/**
 * Register the Autodesk Viewer instance for global access.
 * Call this from where the viewer is created (e.g., RevitForgeViewer).
 * @param {GuiViewer3D|null} viewer - The Autodesk.Viewing.GuiViewer3D instance or null to clear
 * @returns {void}
 */
export function setViewerInstance(viewer) {
	viewerInstance = viewer;
	// Maintain legacy global access for components still using window.viewer
	const globalWindow = /** @type {Window & { viewer?: GuiViewer3D|null }} */ (
		window
	);
	globalWindow.viewer = viewer || null;
}

/**
 * Custom hook to access the Autodesk Viewer instance and highlight objects.
 * @returns {{ viewer: GuiViewer3D|null, highlightObjects: (objectIds: number[]|number) => void }}
 */
export function useViewer() {
	/**
	 * Highlight objects in the viewer by their dbIds.
	 * @param {number[]|number} objectIds - Array or single dbId to highlight
	 * @returns {void}
	 */
	const highlightObjects = (objectIds) => {
		if (!viewerInstance) return;

		const dbIds = Array.isArray(objectIds) ? objectIds : [objectIds];

		viewerInstance.isolate(dbIds);
		viewerInstance.fitToView(dbIds);
		viewerInstance.clearSelection();
		viewerInstance.select(dbIds);
	};

	return { viewer: viewerInstance, highlightObjects };
}
